<template>
  <div class="all">
    <div class="menu">
      <button @click="setView('main')">Главная</button>
      <button @click="setView('tasks')">Задания</button>
      <button @click="setView('map')">Карта</button>
    </div>

    <div class="content">
      <!-- Главная -->
      <div v-if="currentView === 'main'" class="view-content">
        {{ viewContent }}
      </div>

      <!-- Задания -->
      <div v-if="currentView === 'tasks'" class="tasks-view">
        <h2>Список заданий</h2>
        <ul>
          <li
            v-for="(task, index) in visibleTasks"
            :key="index"
            :class="{ done: task.completed }"
          >
            <div class="task-header">
              <strong>{{ task.title }}</strong>
              <button
                v-if="!task.completed"
                @click="completeTask(task.id)"
              >
                Выполнить
              </button>
              <span v-else class="completed-label">✅ Выполнено</span>
            </div>
            <p>{{ task.description }}</p>
          </li>
        </ul>

        <p v-if="allTasksCompleted" class="final-message">
          🎉 Все задания выполнены! Поздравляем!
        </p>
      </div>

      <!-- Карта -->
      <div v-if="currentView === 'map'" class="map-view">
        <h1>Карта заданий</h1>
        <div class="map-container">
          <MapComponent />
        </div>
        <div class="map-description">
          На карте показаны локации, где можно найти новые задания.
        </div>
      </div>
    </div>

    <div class="footer">
      <h2>Прогресс: {{ completedCount }} / {{ tasks.length }}</h2>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import MapComponent from '../components/MapComponent.vue';

// текущее состояние
const currentView = ref('main');
const viewContent = ref('Добро пожаловать в QrQuest! Сканируйте QR-коды, чтобы выполнять задания и открывать новые.');
const progress = ref(0);

// список всех заданий
const tasks = ref([
  { id: 1, title: 'Задание: Найди QR-код у входа', description: 'Сканируй код на стенде.', completed: false, unlocked: true },
  { id: 2, title: 'Задание: Ответь на вопрос по математике', description: 'После сканирования выбери правильный ответ.', completed: false, unlocked: false },
  { id: 3, title: 'Задание: Реши задачу', description: 'Реши уравнение x + 2 = 5.', completed: false, unlocked: false },
  { id: 4, title: 'Задание: Лаборатория', description: 'Посети лабораторию и отметь QR.', completed: false, unlocked: false },
  { id: 5, title: 'Задание: Математика', description: 'Реши уравнение 2x = 8.', completed: false, unlocked: false },
  { id: 6, title: 'Задание: математические законы', description: 'Ответь на вопросы по математике.', completed: false, unlocked: false },
  { id: 7, title: 'Задание: векторы', description: 'найди и отметь QR.', completed: false, unlocked: false },
  { id: 8, title: 'Задание: центры масс', description: 'найди и отметь QR.', completed: false, unlocked: false },
  { id: 9, title: 'Задание: линейная зависимость', description: 'найди и отметь QR.', completed: false, unlocked: false },
  { id: 10, title: 'Финальное задание', description: 'Поздравляем! Вы прошли весь квест!', completed: false, unlocked: false },
]);

// вычисляем, какие задания доступны (unlocked)
const visibleTasks = computed(() => tasks.value.filter(t => t.unlocked));

// сколько выполнено
const completedCount = computed(() => tasks.value.filter(t => t.completed).length);
const allTasksCompleted = computed(() => tasks.value.every(t => t.completed));

// переход между страницами
function setView(view) {
  currentView.value = view;
  viewContent.value = view === 'main'
    ? 'Добро пожаловать в QrQuest! Сканируйте QR-коды, чтобы выполнять задания и открывать новые.'
    : '';
}

// логика открытия заданий
function completeTask(id) {
  const task = tasks.value.find(t => t.id === id);
  if (!task || task.completed) return;

  task.completed = true;

  // логика открытия новых заданий
  switch (id) {
    case 1:
      unlockTasks([2]); // после 1 открывается 2
      break;
    case 2:
      unlockTasks([3, 4]); // после 2 открываются 3 и 4
      break;
    case 4:
      unlockTasks([8]); // после 4 открывается 8
      break;
    case 8:
      unlockTasks([9]); // после 8 открывается 9
      break;
    case 9:
      unlockTasks([10]); // после 9 — финальное
      break;
  }

  progress.value = completedCount.value;
}

// функция открытия заданий по id
function unlockTasks(ids) {
  ids.forEach(id => {
    const task = tasks.value.find(t => t.id === id);
    if (task) task.unlocked = true;
  });
}
</script>

<style scoped>
.all {
  display: flex;
  flex-direction: column;
  height: 80vh;
  width: 100vw;
  max-height: 1200px;
  max-width: 800px;
  overflow: hidden;
}

.menu {
  background-color: #333;
  color: white;
  display: flex;
  gap: 1rem;
  justify-content: space-evenly;
  flex-shrink: 0;
  padding: 0.5rem;
}

button {
  background-color: #555;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 1rem;
  border-radius: 4px;
  transition: background-color 0.3s;
  width: 100%;
}

button:hover {
  background-color: #777;
}

.content {
  flex: 1;
  padding: 1rem;
  margin: 5px 0;
  border: #333 1px solid;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0; /* Важно для flex-контейнеров */
}

/* Главная страница */
.view-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* Контейнер заданий занимает всё доступное место и имеет скролл */
.tasks-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tasks-view h2 {
  margin: 0 0 1rem 0;
  flex-shrink: 0;
}

.tasks-view ul {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
}

.tasks-view li {
  background: #f9f9f9;
  margin: 0.5rem 0;
  padding: 0.7rem;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.tasks-view li.done {
  background-color: #e0ffe0;
  border-color: #8bc34a;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.completed-label {
  color: green;
  font-weight: bold;
}

.final-message {
  text-align: center;
  font-weight: bold;
  color: #4caf50;
  margin-top: 1rem;
  flex-shrink: 0;
}

/* Стили для карты */
.map-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 0.5rem;
}

.map-view h1 {
  margin: 0;
  font-size: 1.5rem;
  flex-shrink: 0;
  text-align: center;
}

.map-container {
  flex: 1;
  min-height: 0;
  position: relative;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.map-description {
  margin: 0;
  flex-shrink: 0;
  font-size: 0.9rem;
  text-align: center;
  color: #666;
}

.footer {
  background-color: #333;
  color: white;
  padding: 1rem;
  flex-shrink: 0;
  text-align: center;
}







</style>